import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../../app/providers/AuthProvider';
import { COLORS } from '../../../../core/constants/colors';

interface AdminLayoutProps {
  children: React.ReactNode;
  pageTitle?: string;
  pageDescription?: string;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children, pageTitle, pageDescription }) => {
  const { authState, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(new Set());

  const getInitial = () => {
    return authState.user?.full_name?.charAt(0).toUpperCase() || authState.user?.email?.charAt(0).toUpperCase() || 'A';
  };

  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const toggleMenu = (menuKey: string) => {
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuKey)) {
        newSet.delete(menuKey);
      } else {
        newSet.add(menuKey);
      }
      return newSet;
    });
  };

  const isMenuExpanded = (menuKey: string) => {
    return expandedMenus.has(menuKey);
  };

  const isEnrichmentActive = () => {
    return (
      location.pathname.startsWith('/admin/enrichment') ||
      location.pathname.startsWith('/admin/web-enrichment') ||
      location.pathname.startsWith('/admin/linkedin-enrichment')
    );
  };

  // Auto-expand enrichment menu if on enrichment page
  useEffect(() => {
    const isActive = 
      location.pathname.startsWith('/admin/enrichment') ||
      location.pathname.startsWith('/admin/web-enrichment') ||
      location.pathname.startsWith('/admin/linkedin-enrichment');
    
    if (isActive && !expandedMenus.has('/admin/enrichment')) {
      setExpandedMenus((prev) => new Set(prev).add('/admin/enrichment'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const adminNavItems = [
    {
      name: 'Dashboard',
      path: '/admin',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
        </svg>
      )
    },
    {
      name: 'Company Management',
      path: '/admin/companies',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Partner Management',
      path: '/admin/users',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M13 7a3 3 0 11-6 0 3 3 0 016 0z" />
          <path fillRule="evenodd" d="M4 13a4 4 0 014-4h4a4 4 0 014 4v1a1 1 0 01-1 1H5a1 1 0 01-1-1v-1z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Documents',
      path: '/admin/documents',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Queries',
      path: '/admin/queries',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Forms',
      path: '/admin/forms',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
          <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Custom Fields',
      path: '/admin/custom-fields',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'Enrichment',
      path: '/admin/enrichment',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      ),
      isDropdown: true,
      subItems: [
        {
          name: 'Company Enrichment',
          path: '/admin/enrichment',
        },
        {
          name: 'Web Enrichment',
          path: '/admin/web-enrichment',
    },
    {
      name: 'LinkedIn Enrichment',
      path: '/admin/linkedin-enrichment',
        },
      ],
    },
    {
      name: 'Billing Plans',
      path: '/admin/billing',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
          <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'AI Assistant',
      path: '/admin/ai-assistant',
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Tracxn Verification',
      path: '/admin/tracxn-verification',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'ETL Tasks',
      path: '/admin/etl-tasks',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      name: 'System Health',
      path: '/admin/system-health',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    },
  ];

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Sidebar */}
      <div
        className={`fixed h-full flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'w-20' : 'w-64'
          }`}
        style={{
          backgroundColor: COLORS.admin.primary,
          margin: '12px',
          height: 'calc(100vh - 24px)',
          borderRadius: '16px',
          overflow: 'hidden'
        }}
      >
        {/* Logo & Toggle */}
        <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
          {!isSidebarCollapsed && (
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => navigate('/dashboard')}
            >
              <img
                src="/new_logo+white.png"
                alt="Portfolio Indexing System"
                className="h-16"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSidebarCollapsed(!isSidebarCollapsed);
            }}
            className="text-white hover:bg-white/10 p-2 rounded-lg transition-all flex-shrink-0"
            title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {isSidebarCollapsed ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              )}
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {adminNavItems.map((item: any) => {
            const hasSubItems = item.isDropdown && item.subItems;
            const isExpanded = hasSubItems && isMenuExpanded(item.path);
            const isItemActive = hasSubItems ? isEnrichmentActive() : isActive(item.path);

            if (hasSubItems && !isSidebarCollapsed) {
              return (
                <div key={item.path}>
                  <button
                    onClick={() => toggleMenu(item.path)}
                    className={`w-full flex items-center justify-between gap-3 py-3.5 text-white transition-all text-left ${
                      isItemActive ? 'bg-white/10' : 'hover:bg-white/5'
                    } px-6`}
                    style={{
                      borderLeft: isItemActive ? '4px solid white' : '4px solid transparent',
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="flex-shrink-0">{item.icon}</div>
                      <span className="font-medium text-[15px]">{item.name}</span>
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isExpanded && (
                    <div className="pl-6">
                      {item.subItems.map((subItem: any) => (
                        <button
                          key={subItem.path}
                          onClick={() => navigate(subItem.path)}
                          className={`w-full flex items-center gap-3 py-2.5 text-white transition-all text-left ${
                            isActive(subItem.path) ? 'bg-white/10' : 'hover:bg-white/5'
                          } px-6`}
                          style={{
                            borderLeft: isActive(subItem.path) ? '4px solid white' : '4px solid transparent',
                          }}
                        >
                          <div className="w-2 h-2 rounded-full bg-white/40"></div>
                          <span className="font-medium text-sm">{subItem.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 py-3.5 text-white transition-all text-left ${
                  isItemActive ? 'bg-white/10' : 'hover:bg-white/5'
                } ${isSidebarCollapsed ? 'px-4 justify-center' : 'px-6'}`}
              style={{
                  borderLeft: isItemActive ? '4px solid white' : '4px solid transparent',
              }}
              title={isSidebarCollapsed ? item.name : ''}
            >
              <div className="flex-shrink-0">{item.icon}</div>
              {!isSidebarCollapsed && (
                <span className="font-medium text-[15px]">{item.name}</span>
              )}
            </button>
            );
          })}
        </nav>

        {/* Footer */}
        {!isSidebarCollapsed && (
          <div className="p-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <div className="flex items-center gap-2 text-white text-xs" style={{ opacity: 0.6 }}>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span>AI-powered indexing • Smart document search</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarCollapsed ? 'ml-[92px]' : 'ml-[268px]'
          }`}
        style={{ marginTop: '12px' }}
      >
        {/* Top Header - Sticky */}
        <div className="bg-white border-b sticky z-40" style={{ borderColor: '#E5E7EB', top: '12px' }}>
          <div className="px-8 py-4 flex items-center justify-between">
            {/* Page Title & Description */}
            {pageTitle && (
              <div className="flex-1">
                <h1 className="text-2xl font-bold mb-1" style={{ color: '#1F2937' }}>
                  {pageTitle}
                </h1>
                {pageDescription && (
                  <p className="text-sm" style={{ color: '#6B7280' }}>
                    {pageDescription}
                  </p>
                )}
              </div>
            )}

            {/* Top Right Actions */}
            <div className="flex items-center gap-4">
              <a
                href="https://pi.vgg.app/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium hover:underline"
                style={{ color: '#6B7280' }}
              >
                Docs
              </a>

              {/* User Profile */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold cursor-pointer hover:opacity-80 transition-all"
                  style={{ backgroundColor: COLORS.admin.primary }}
                >
                  {getInitial()}
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border py-1" style={{ borderColor: '#E5E7EB', zIndex: 50 }}>
                    <div className="px-4 py-3 border-b" style={{ borderColor: '#E5E7EB' }}>
                      <p className="font-semibold text-sm" style={{ color: '#1F2937' }}>
                        {authState.user?.full_name || 'Admin'}
                      </p>
                      <p className="text-xs" style={{ color: '#6B7280' }}>
                        {authState.user?.email}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate('/dashboard');
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-all"
                      style={{ color: '#374151' }}
                    >
                      User Dashboard
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                        navigate('/login');
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-all"
                      style={{ color: '#DC2626' }}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

